/* eslint-disable jsx-a11y/label-has-associated-control */
import './style.scss';

import _ from 'lodash';
import * as pt from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Checkbox, Dimmer, Icon, Loader, Popup } from 'semantic-ui-react';

import { DEFAULT_ANIMAL } from 'app/components/Dropzone/constants';
import { FlatPanelStateContext, getCurrentDetector } from 'app/components/FlatPanelStateProvider';
import ConstantSelector from 'app/components/XRayAreaView/ConstantSelector';
import FocusIcon from 'app/components/XRayAreaView/FocusIcon';
import { SPECIES, SpeciesType } from 'app/constants/species';
import useDidUpdateEffect from 'app/hooks/useDidUpdateEffect';
import XRayGeneratorHistoryContext from 'app/providers/XRayGeneratorHistoryProvider/context';
import {
  XRayGeneratorControllerContext,
  XRayGeneratorStateContext,
} from 'app/providers/XRayGeneratorProvider';
import {
  selectAcquisitionConstants,
  selectTelemetry,
} from 'app/redux/xrayGeneratorConfiguration/reducer';
import { DETECTOR_EVENTS } from 'app/types/xray';
import ACQUISITION_CONSTANTS, {
  AcquisitionConstantsByProfile,
  MAX_KV,
  MAX_THICKNESS,
  MIN_KV,
  MIN_THICKNESS,
  getKVLimits,
  getMALimits,
} from 'app/utils/acquisitionConstants';
import { getAnatomicRegionFromString, MainRegion } from 'app/utils/xrayRegions';
import {
  EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
  EVENT_GENERATOR_THICKNESS,
  READY_STATUS,
  SET_RAD_RETRY_DELAY,
  UNIT_STATUS,
} from 'app/xray/generator/constants';

import { ReactComponent as Bird } from './animal_parts/bird.svg';
import { ReactComponent as BirdEntireBody } from './animal_parts/bird_entire_body.svg';
import { ReactComponent as Cat } from './animal_parts/cat.svg';
import { ReactComponent as CatAbdomen } from './animal_parts/cat_abdomen.svg';
import { ReactComponent as CatEntireBody } from './animal_parts/cat_entire_body.svg';
import { ReactComponent as CatMembrePelvienGauche } from './animal_parts/cat_pelvic_limb_left.svg';
import { ReactComponent as CatMembrePelvienDroit } from './animal_parts/cat_pelvic_limb_right.svg';
import { ReactComponent as CatCrane } from './animal_parts/cat_skull.svg';
import { ReactComponent as CatRachis } from './animal_parts/cat_spine.svg';
import { ReactComponent as CatMembreThoraciqueGauche } from './animal_parts/cat_thoracic_limb_left.svg';
import { ReactComponent as CatMembreThoraciqueDroit } from './animal_parts/cat_thoracic_limb_right.svg';
import { ReactComponent as CatThorax } from './animal_parts/cat_thorax.svg';
import { ReactComponent as CatTail } from './animal_parts/cat_tail.svg';
import { ReactComponent as Dog } from './animal_parts/dog.svg';
import { ReactComponent as DogAbdomen } from './animal_parts/dog_abdomen.svg';
import { ReactComponent as DogEntireBody } from './animal_parts/dog_entire_body.svg';
import { ReactComponent as DogMembrePelvienGauche } from './animal_parts/dog_pelvic_limb_left.svg';
import { ReactComponent as DogMembrePelvienDroit } from './animal_parts/dog_pelvic_limb_right.svg';
import { ReactComponent as DogCrane } from './animal_parts/dog_skull.svg';
import { ReactComponent as DogRachis } from './animal_parts/dog_spine.svg';
import { ReactComponent as DogTail } from './animal_parts/dog_tail.svg';
import { ReactComponent as DogMembreThoraciqueGauche } from './animal_parts/dog_thoracic_limb_left.svg';
import { ReactComponent as DogMembreThoraciqueDroit } from './animal_parts/dog_thoracic_limb_right.svg';
import { ReactComponent as DogThorax } from './animal_parts/dog_thorax.svg';
import { ReactComponent as Ferret } from './animal_parts/ferret.svg';
import { ReactComponent as FerretEntireBody } from './animal_parts/ferret_entire_body.svg';
import { ReactComponent as Rabbit } from './animal_parts/rabbit.svg';
import { ReactComponent as RabbitEntireBody } from './animal_parts/rabbit_entire_body.svg';
import { ReactComponent as Reptile } from './animal_parts/reptile.svg';
import { ReactComponent as ReptileEntireBody } from './animal_parts/reptile_entire_body.svg';
import { ReactComponent as Rodent } from './animal_parts/rodent.svg';
import { ReactComponent as RodentEntireBody } from './animal_parts/rodent_entire_body.svg';
import onAcquisitionConstantChange from 'app/components/XRayAreaView/onAcquisitionConstantChange';
import { AcquisitionConstants, CoreAcquisitionConstants } from 'app/interfaces/Image';
import { Detector } from 'app/interfaces/DetectorManager';
import { Patient } from 'app/interfaces/Patient';

const THICKNESS_REQUEST_INTERVAL = 1000;
const SET_RAD_DEBOUNCE_MS = 1000;

const SPECIES_BODY_SVG = {
  cat: Cat,
  dog: Dog,
  rabbit: Rabbit,
  ferret: Ferret,
  rodent: Rodent,
  bird: Bird,
  reptile: Reptile,
  animal: Dog,
  other: Dog,
} as const;

const SPECIES_BODY_BODY_PARTS_SVG = {
  dog: {
    thorax: DogThorax,
    abdomen: DogAbdomen,
    crane: DogCrane,
    rachis: DogRachis,
    membre_pelvien_gauche: DogMembrePelvienGauche,
    membre_pelvien_droit: DogMembrePelvienDroit,
    membre_thoracique_gauche: DogMembreThoraciqueGauche,
    membre_thoracique_droit: DogMembreThoraciqueDroit,
    tail: DogTail,
    entire_body: DogEntireBody,
  },
  cat: {
    thorax: CatThorax,
    abdomen: CatAbdomen,
    crane: CatCrane,
    rachis: CatRachis,
    membre_pelvien_gauche: CatMembrePelvienGauche,
    membre_pelvien_droit: CatMembrePelvienDroit,
    membre_thoracique_gauche: CatMembreThoraciqueGauche,
    membre_thoracique_droit: CatMembreThoraciqueDroit,
    tail: CatTail,
    entire_body: CatEntireBody,
  },
  rabbit: {
    thorax: RabbitEntireBody,
    abdomen: RabbitEntireBody,
    crane: RabbitEntireBody,
    rachis: RabbitEntireBody,
    membre_pelvien_gauche: RabbitEntireBody,
    membre_pelvien_droit: RabbitEntireBody,
    membre_thoracique_gauche: RabbitEntireBody,
    membre_thoracique_droit: RabbitEntireBody,
    tail: RabbitEntireBody,
    entire_body: RabbitEntireBody,
  },
  ferret: {
    thorax: FerretEntireBody,
    abdomen: FerretEntireBody,
    crane: FerretEntireBody,
    rachis: FerretEntireBody,
    membre_pelvien_gauche: FerretEntireBody,
    membre_pelvien_droit: FerretEntireBody,
    membre_thoracique_gauche: FerretEntireBody,
    membre_thoracique_droit: FerretEntireBody,
    tail: FerretEntireBody,
    entire_body: FerretEntireBody,
  },
  rodent: {
    thorax: RodentEntireBody,
    abdomen: RodentEntireBody,
    crane: RodentEntireBody,
    rachis: RodentEntireBody,
    membre_pelvien_gauche: RodentEntireBody,
    membre_pelvien_droit: RodentEntireBody,
    membre_thoracique_gauche: RodentEntireBody,
    membre_thoracique_droit: RodentEntireBody,
    tail: RodentEntireBody,
    entire_body: RodentEntireBody,
  },
  bird: {
    thorax: BirdEntireBody,
    abdomen: BirdEntireBody,
    crane: BirdEntireBody,
    rachis: BirdEntireBody,
    membre_pelvien_gauche: BirdEntireBody,
    membre_pelvien_droit: BirdEntireBody,
    membre_thoracique_gauche: BirdEntireBody,
    membre_thoracique_droit: BirdEntireBody,
    tail: BirdEntireBody,
    entire_body: BirdEntireBody,
  },
  reptile: {
    thorax: ReptileEntireBody,
    abdomen: ReptileEntireBody,
    crane: ReptileEntireBody,
    rachis: ReptileEntireBody,
    membre_pelvien_gauche: ReptileEntireBody,
    membre_pelvien_droit: ReptileEntireBody,
    membre_thoracique_gauche: ReptileEntireBody,
    membre_thoracique_droit: ReptileEntireBody,
    tail: ReptileEntireBody,
    entire_body: ReptileEntireBody,
  },

  other: {
    thorax: DogThorax,
    abdomen: DogAbdomen,
    crane: DogCrane,
    rachis: DogRachis,
    membre_pelvien_gauche: DogMembrePelvienGauche,
    membre_pelvien_droit: DogMembrePelvienDroit,
    membre_thoracique_gauche: DogMembreThoraciqueGauche,
    membre_thoracique_droit: DogMembreThoraciqueDroit,
    tail: DogTail,
    entire_body: DogEntireBody,
  },
} as const;

function getSpecieSVG(specie: SpeciesType) {
  return SPECIES_BODY_SVG[specie as keyof typeof SPECIES_BODY_SVG] ?? SPECIES_BODY_SVG.dog;
}

function getSpecieBodyPartSVG(specie: SpeciesType, bodyPart: MainRegion) {
  return (SPECIES_BODY_BODY_PARTS_SVG[specie as keyof typeof SPECIES_BODY_BODY_PARTS_SVG] ??
    SPECIES_BODY_BODY_PARTS_SVG.dog)[bodyPart];
}

const getAcquisitionConstantsMapForRegion = (
  acquisitionConstantsByProfile: AcquisitionConstantsByProfile | undefined,
  anatomicRegion: string
) => {
  const { mainRegion, subRegion } = getAnatomicRegionFromString(anatomicRegion);
  return (
    acquisitionConstantsByProfile?.[`${mainRegion} ${subRegion}`] ??
    acquisitionConstantsByProfile?.[mainRegion] ??
    acquisitionConstantsByProfile?.default ??
    ACQUISITION_CONSTANTS[`${mainRegion} ${subRegion}`] ??
    ACQUISITION_CONSTANTS[mainRegion] ??
    ACQUISITION_CONSTANTS.default
  );
};

type RadSyncState = 'rad_syncing' | 'rad_sync' | 'rad_not_sync';

function RadSyncDisplay({ radSyncState }: { radSyncState: RadSyncState | undefined }) {
  if (!radSyncState) return null;

  const isSyncOngoing = radSyncState === 'rad_syncing';
  const isSync = radSyncState === 'rad_sync';

  const testId = `xray_generator.${radSyncState}`;
  let statusIndicator;
  let popupContent;
  if (isSyncOngoing) {
    statusIndicator = (
      <div data-testid={testId} className="rad-sync-state">
        <Icon name="sync" size="small" loading />
      </div>
    );
    popupContent = <FormattedMessage id="xray_generator.rad.syncing" />;
  } else if (isSync) {
    statusIndicator = (
      <div data-testid={testId} className="rad-sync-state rad-sync-state--sync">
        ●
      </div>
    );
    popupContent = <FormattedMessage id="xray_generator.rad.sync" />;
  } else {
    statusIndicator = (
      <div data-testid={testId} className="rad-sync-state rad-sync-state--not-sync">
        ●
      </div>
    );
    popupContent = <FormattedMessage id="xray_generator.rad.not_sync" />;
  }

  return (
    <Popup
      hoverable
      flowing
      trigger={statusIndicator}
      content={popupContent}
      position="right center"
    />
  );
}

type XRayAreaViewProps = {
  image: {
    acquisitionConstants: AcquisitionConstants;
    anatomicRegion: string;
    isProcessingOnGoing?: boolean;
  };
  onChange: (acquisitionConstants: AcquisitionConstants) => void;
  className?: string;
  animal?: Patient;
  style?: React.CSSProperties | null;
  isFocused?: boolean;
};

function XRayAreaView({
  image,
  onChange,
  className = '',
  animal = DEFAULT_ANIMAL,
  style = null,
  isFocused = false,
}: XRayAreaViewProps) {
  const intl = useIntl();
  const [acquisitionStarted, setAcquisitionStarted] = useState(false);
  const flatPanelStateContext = useContext(FlatPanelStateContext);
  const xRayGeneratorController = useContext(XRayGeneratorControllerContext);
  const xRayGeneratorState = useContext(XRayGeneratorStateContext);
  const isTelemetry = useSelector(selectTelemetry);
  const acquisitionConstantsByProfile = useSelector(selectAcquisitionConstants);
  const xRayGeneratorHistory = useContext(XRayGeneratorHistoryContext);
  const thicknessRequestInterval = useRef<NodeJS.Timeout>();
  const xRayGeneratorControllerRef = useRef<XRayGeneratorController>();
  const displayUpdatedRadParameters =
    useRef<
      (radParameters: { kV: number; mAs: number; mA: number; ms: number; focus: number }) => void
    >();
  const [radSyncState, setRadSyncState] = useState<RadSyncState | undefined>(() =>
    xRayGeneratorController ? 'rad_not_sync' : undefined
  );
  const [isPendingAcquisitionConstantChange, setIsPendingAcquisitionConstantChange] =
    useState(false);
  const setIsPendingAcquisitionConstantChangeRef = useRef(setIsPendingAcquisitionConstantChange);
  const radChangeHandler = useRef<
    (event: Event & { kV: number; mAs: number; mA: number; ms: number; focus: number }) => void
  >((event) => {
    displayUpdatedRadParameters.current(event);
    xRayGeneratorControllerRef.current?.lock(false);
    setIsPendingAcquisitionConstantChangeRef.current(false);
  });

  xRayGeneratorControllerRef.current = xRayGeneratorController;
  setIsPendingAcquisitionConstantChangeRef.current = setIsPendingAcquisitionConstantChange;

  const setRadSyncIfPossible = (radSyncStateToSet: RadSyncState | undefined) => {
    if (!xRayGeneratorControllerRef.current) {
      setRadSyncState(undefined);
    } else {
      setRadSyncState(radSyncStateToSet);
    }
  };
  const onManualFieldBlur = () => setIsPendingAcquisitionConstantChange(false);

  const isGeneratorControlAllowed = !acquisitionStarted && isFocused;

  const imageAcquisitionConstants = image?.acquisitionConstants;
  const isAutomaticConstants = image?.acquisitionConstants?.isAutomaticConstants ?? true;
  const requestedRadParameters = useMemo(
    () => ({
      ms: Math.round((imageAcquisitionConstants?.s ?? 0) * 1000),
      mA: imageAcquisitionConstants?.mA,
      kV: imageAcquisitionConstants?.kV,
    }),
    [imageAcquisitionConstants]
  );

  const acquisitionConstantsMap = useMemo(
    () => getAcquisitionConstantsMapForRegion(acquisitionConstantsByProfile, image?.anatomicRegion),
    [acquisitionConstantsByProfile, image?.anatomicRegion]
  );

  const maxThickness = useMemo(() => {
    if (acquisitionConstantsMap) {
      return _(acquisitionConstantsMap).maxBy('thickness')?.thickness ?? MAX_THICKNESS;
    }
    return MAX_THICKNESS;
  }, [acquisitionConstantsMap]);

  const minThickness = useMemo(() => {
    if (acquisitionConstantsMap) {
      return _(acquisitionConstantsMap).minBy('thickness')?.thickness ?? MIN_THICKNESS;
    }
    return MIN_THICKNESS;
  }, [acquisitionConstantsMap]);

  const validateRad = (acquisitionConstants: { kV: number; mA: number; ms: number }) => {
    if (!xRayGeneratorControllerRef.current) return;

    setRadSyncIfPossible('rad_syncing');
    xRayGeneratorControllerRef.current?.setRadParameters(acquisitionConstants);

    setIsPendingAcquisitionConstantChange(false);
  };

  const [setRadParametersDebounced] = useState(() => _.debounce(validateRad, SET_RAD_DEBOUNCE_MS));

  const triggerParameterChange = useCallback(
    (newAcquisitionConstants: AcquisitionConstants) => {
      if (radSyncState === 'rad_syncing') return;
      if (_.isEqual(imageAcquisitionConstants, newAcquisitionConstants)) return;
      onChange(newAcquisitionConstants);
    },
    [imageAcquisitionConstants, onChange, radSyncState]
  );

  const onAutomaticConstantswitch = useCallback<React.ComponentProps<typeof Checkbox>['onChange']>(
    (_event, { checked }) =>
      onChange({ ...imageAcquisitionConstants, isAutomaticConstants: checked }),
    [onChange, imageAcquisitionConstants]
  );

  // TODO Remove
  const onAcquisitionStart = useCallback(() => setAcquisitionStarted(true), []);
  const onAcquisitionEnd = useCallback(() => setAcquisitionStarted(false), []);

  const removeListeners = (detector: Detector) => {
    detector?.removeListener(DETECTOR_EVENTS.ACQUISITION_START, onAcquisitionStart);
    detector?.removeListener(DETECTOR_EVENTS.ACQUISITION_END, onAcquisitionEnd);
  };

  const appliedParameters = useMemo(
    () => _.pick(xRayGeneratorState?.radParameters, ['kV', 'ms', 'mA']),
    [xRayGeneratorState?.radParameters]
  );

  displayUpdatedRadParameters.current = (radParameters) => {
    const secondes = radParameters.ms / 1000.0;
    onChange({
      ...imageAcquisitionConstants,
      ..._.pick(radParameters, ['kV', 'mA', 'focus']),
      s: secondes,
    });
  };

  const planRadParametersSetOnceChangeSettle = () => {
    if (!xRayGeneratorController) return;
    if (requestedRadParameters.kV === 0) return;
    if (!isGeneratorControlAllowed) return;

    if (xRayGeneratorState?.unitStatus !== UNIT_STATUS.STANDBY_MODE) {
      if (!radSyncState) {
        setRadSyncIfPossible('rad_not_sync');
      }
      return;
    }

    const isStatusReady = xRayGeneratorState?.readyStatus === READY_STATUS.READY;

    const isRequestedParametersApplied = _.isEqual(appliedParameters, requestedRadParameters);

    setRadParametersDebounced.cancel();
    if (isRequestedParametersApplied && isStatusReady) {
      setRadSyncIfPossible('rad_sync');
      return;
    }

    if (radSyncState === 'rad_syncing') return;
    if (isStatusReady) xRayGeneratorController?.lock(true);
    setRadSyncIfPossible('rad_not_sync');
    if (isPendingAcquisitionConstantChange) return;

    setRadParametersDebounced(requestedRadParameters);
  };

  useEffect(planRadParametersSetOnceChangeSettle, [
    isGeneratorControlAllowed,
    radSyncState,
    isPendingAcquisitionConstantChange,
    requestedRadParameters,
    xRayGeneratorController,
    xRayGeneratorState?.unitStatus,
    xRayGeneratorState?.readyStatus,
  ]);

  const registerRadChangeListenerWhenInControl = () => {
    if (isGeneratorControlAllowed) {
      xRayGeneratorController?.addEventListener(
        EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
        radChangeHandler.current
      );
      return () => {
        xRayGeneratorController?.removeEventListener(
          EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
          radChangeHandler.current
        );
      };
    }
  };
  useEffect(registerRadChangeListenerWhenInControl, [
    isGeneratorControlAllowed,
    xRayGeneratorController,
  ]);

  const cancelRadOperationsWhenNotActive = () => {
    if (isGeneratorControlAllowed) return;

    setRadParametersDebounced.cancel();
    if (!xRayGeneratorController) {
      setRadSyncIfPossible(undefined);
      return;
    }

    setRadSyncIfPossible('rad_not_sync');
  };
  useEffect(cancelRadOperationsWhenNotActive, [isGeneratorControlAllowed, xRayGeneratorController]);

  const retrySettingRadIfSyncTooLong = () => {
    if (radSyncState !== 'rad_syncing') return undefined;

    const radRetryInterval = setInterval(
      () => validateRad(requestedRadParameters),
      SET_RAD_RETRY_DELAY
    );
    return () => clearInterval(radRetryInterval);
  };
  useEffect(retrySettingRadIfSyncTooLong, [requestedRadParameters, radSyncState]);

  const unsetRadSyncStatusOnDisconnection = () => {
    setRadSyncIfPossible(undefined);
  };
  useEffect(unsetRadSyncStatusOnDisconnection, [xRayGeneratorController]);

  const onManualValueChange = useCallback(
    (constantName: keyof CoreAcquisitionConstants | 'mAs', value: number) => {
      const updatedParameters = onAcquisitionConstantChange(
        image.acquisitionConstants,
        constantName,
        value
      );
      triggerParameterChange(updatedParameters);
      setIsPendingAcquisitionConstantChange(true);
    },
    [triggerParameterChange, image?.acquisitionConstants]
  );

  const onValueChange = useCallback(
    (constantName: keyof CoreAcquisitionConstants | 'mAs', value: number) => {
      const updatedParameters = onAcquisitionConstantChange(
        image.acquisitionConstants,
        constantName,
        value
      );
      triggerParameterChange(updatedParameters);
    },
    [triggerParameterChange, image?.acquisitionConstants]
  );

  const onThicknessChange = useCallback(
    (_constantName: any, thickness: number) => {
      const roundedThickness = Math.round(thickness);
      const max = _(acquisitionConstantsMap).maxBy('thickness');
      const min = _(acquisitionConstantsMap).minBy('thickness');
      const boundedThickness = Math.min(max.thickness, Math.max(min.thickness, roundedThickness));
      if (!isAutomaticConstants) {
        triggerParameterChange({
          ...imageAcquisitionConstants,
          thickness: boundedThickness,
        });
        return;
      }

      const acquisitionConstants = _(acquisitionConstantsMap).find({ thickness: boundedThickness });
      if (acquisitionConstants) {
        triggerParameterChange({
          ...imageAcquisitionConstants,
          ...acquisitionConstants,
          thickness: boundedThickness,
        });
        return;
      }

      triggerParameterChange({
        ...imageAcquisitionConstants,
        ...(boundedThickness > max.thickness ? max : min),
        thickness: boundedThickness,
      });
    },
    [
      triggerParameterChange,
      acquisitionConstantsMap,
      image,
      isAutomaticConstants,
      imageAcquisitionConstants,
    ]
  );

  const currentDetector = getCurrentDetector(flatPanelStateContext);
  const registerDetectorAcquisitionListener = () => {
    currentDetector?.on(DETECTOR_EVENTS.ACQUISITION_START, onAcquisitionStart);
    currentDetector?.on(DETECTOR_EVENTS.ACQUISITION_END, onAcquisitionEnd);

    return () => {
      removeListeners(currentDetector);
      setAcquisitionStarted(false);
    };
  };
  useEffect(registerDetectorAcquisitionListener, [currentDetector]);

  const initializeNullAcquisitionConstants = () => {
    const { thickness, kV } = image.acquisitionConstants;
    if (!kV) {
      onThicknessChange(undefined, thickness || 10);
    }
  };
  useEffect(initializeNullAcquisitionConstants, [image.acquisitionConstants, image.anatomicRegion]);

  const listenForThicknessEvents = () => {
    if (!isGeneratorControlAllowed) return undefined;
    const callback = ({ thickness }: Event & { thickness: number }) => {
      if (thickness === 0) return;
      onThicknessChange(undefined, thickness);
    };
    xRayGeneratorController?.on(EVENT_GENERATOR_THICKNESS, callback);
    return () => xRayGeneratorController?.removeEventListener(EVENT_GENERATOR_THICKNESS, callback);
  };
  useEffect(listenForThicknessEvents, [
    isGeneratorControlAllowed,
    xRayGeneratorController,
    onThicknessChange,
    isAutomaticConstants,
    imageAcquisitionConstants,
  ]);

  const startThicknessRequestsLoop = () => {
    if (!isGeneratorControlAllowed) return undefined;
    if (!isTelemetry) return undefined;

    xRayGeneratorController?.requestThickness();

    thicknessRequestInterval.current = setInterval(
      () => xRayGeneratorController?.requestThickness(),
      THICKNESS_REQUEST_INTERVAL
    );
    return () => clearInterval(thicknessRequestInterval.current);
  };
  useEffect(startThicknessRequestsLoop, [
    isGeneratorControlAllowed,
    isTelemetry,
    xRayGeneratorController,
  ]);

  const restoreisAutomaticConstants = () => {
    if (!isAutomaticConstants) return;

    setIsPendingAcquisitionConstantChange(false);
    onThicknessChange(undefined, imageAcquisitionConstants?.thickness);
  };
  useDidUpdateEffect(restoreisAutomaticConstants, [isAutomaticConstants]);

  useEffect(() => {
    if (!isFocused) return;
    xRayGeneratorHistory?.setAcquisitionConstants(imageAcquisitionConstants);
  }, [imageAcquisitionConstants, isFocused]);

  const { specie } = animal;
  const { anatomicRegion, isProcessingOnGoing } = image;
  const { thickness, kV, mA, s, focus } = image.acquisitionConstants;
  const mAs = +(mA * s).toFixed(1);

  const { mainRegion, subRegion, view } = getAnatomicRegionFromString(anatomicRegion);

  const BodySVG = getSpecieSVG(specie);
  const TargetedBodyPartSVG = getSpecieBodyPartSVG(specie, mainRegion);

  const isRadSync = radSyncState === 'rad_sync';
  const isPrepState =
    xRayGeneratorState?.unitStatus === UNIT_STATUS.FIRST_TIME || xRayGeneratorState?.prepStarted;
  const kvLimits = getKVLimits(kV);

  if (!isFocused) return null;

  return (
    <div className={`xray-area-view ${className}`} style={style}>
      <Dimmer active={acquisitionStarted || isProcessingOnGoing}>
        <Loader />
      </Dimmer>
      <div className="body-part-title">
        <div className="main-part">
          <FormattedMessage id={`exam.body_part.${mainRegion}`} />
        </div>
        <div className="secondary-part">
          {subRegion && <FormattedMessage id={`exam.body_part.${subRegion}`} />}
          {subRegion && view && ' '}
          {view && <FormattedMessage id={`exam.view.${view}`} />}
        </div>
      </div>
      <div className="animal-xray">
        <div>
          <BodySVG className="body-full" />
          <TargetedBodyPartSVG className="body-part" />
        </div>
      </div>
      <form
        className="constants-area"
        onSubmit={(e) => {
          validateRad(requestedRadParameters);
          e.preventDefault();
        }}
      >
        <div className="constants-container" data-testid="xray-constants">
          <RadSyncDisplay radSyncState={radSyncState} />
          {isAutomaticConstants && (
            <ConstantSelector
              className="thickness"
              onChange={onThicknessChange}
              value={thickness}
              constantName="thickness"
              data-testid="thickness-selector"
              step="1"
              type="dropdown"
              min={minThickness}
              max={maxThickness}
              disabled={isPrepState}
            />
          )}

          <div className="constants-right-list">
            {!isAutomaticConstants ? (
              <ConstantSelector
                className="electrical-constant"
                onChange={onValueChange}
                data-testid="kV-selector"
                value={kV}
                type="dropdown"
                constantName="kV"
                step="1"
                min={MIN_KV}
                max={MAX_KV}
                style={{ width: '7em' }}
                disabled={isAutomaticConstants || isPrepState}
              />
            ) : (
              <ConstantSelector
                className="electrical-constant"
                onChange={onValueChange}
                value={kV}
                data-testid="kV-selector"
                constantName="kV"
                style={{ width: '7em' }}
                disabled
              />
            )}

            <ConstantSelector
              className="electrical-constant"
              value={mAs}
              data-testid="mAs-selector"
              constantName="mAs"
              onChange={onManualValueChange}
              onBlur={onManualFieldBlur}
              step="0.1"
              style={{ width: '7em' }}
              disabled={isAutomaticConstants || isPrepState}
            />

            {!isAutomaticConstants && (
              <ConstantSelector
                className="electrical-constant"
                value={mA}
                data-testid="mA-selector"
                onChange={onValueChange}
                type="dropdown"
                constantName="mA"
                options={_.map(kvLimits.mAsLimits, (value, key) => {
                  const disabled = mAs > value;
                  const title = disabled
                    ? intl.formatMessage(
                        { id: 'xray_generator.rad.mAs_limit_exceeded' },
                        { mAsLimit: value.toFixed(0) }
                      )
                    : undefined;
                  const intergerMA = parseInt(key);
                  return { key: intergerMA, value: intergerMA, text: intergerMA, disabled, title };
                })}
                style={{ width: '7em' }}
                disabled={isPrepState}
              />
            )}
            {!isAutomaticConstants && (
              <ConstantSelector
                className="electrical-constant"
                value={s}
                data-testid="seconds-selector"
                onChange={onManualValueChange}
                onBlur={onManualFieldBlur}
                constantName="s"
                step="0.001"
                style={{ width: '9em' }}
                disabled={isPrepState}
              />
            )}

            <FocusIcon focus={focus} />
          </div>
        </div>
        <div className="xray-area-view__sub-area">
          <Checkbox
            as="label"
            className="xray-area-view__sub-area__input-mode"
            checked={isAutomaticConstants}
            toggle
            onChange={onAutomaticConstantswitch}
            label={
              <label>
                <FormattedMessage
                  id={
                    isAutomaticConstants
                      ? 'xray_generator.rad.automatic'
                      : 'xray_generator.rad.manual'
                  }
                />
              </label>
            }
          />

          {!isAutomaticConstants &&
            xRayGeneratorState?.connectionState === 'connected' &&
            !isRadSync &&
            isPendingAcquisitionConstantChange && (
              <Button primary={!isRadSync} className="validate-rad" type="submit">
                <FormattedMessage id="xray_generator.validate_rad" />
              </Button>
            )}
          <div />
        </div>
      </form>
    </div>
  );
}

XRayAreaView.propTypes = {
  image: pt.shape({
    anatomicRegion: pt.string.isRequired,
    acquisitionConstants: pt.shape({
      thickness: pt.number.isRequired,
      kV: pt.number.isRequired,
      mA: pt.number.isRequired,
      s: pt.number.isRequired,
      mAs: pt.number,
      focus: pt.oneOfType([pt.bool, pt.number]),
    }),
    isProcessingOnGoing: pt.bool,
  }).isRequired,
  isProcessingOnGoing: pt.bool,
  animal: pt.shape({ specie: pt.oneOf(SPECIES) }),
  onChange: pt.func.isRequired,
  className: pt.string,
  style: pt.shape({}),
  isFocused: pt.bool,
};

export default XRayAreaView;
